/** Tempo máximo para considerar falha ao carregar imagem remota (timeout de rede sem disparar onError rápido). */
export const REMOTE_IMAGE_LOAD_TIMEOUT_MS = 12_000;

const CACHE_KEY = 'failed_image_urls';
const CACHE_EXPIRY_KEY = 'failed_image_urls_expiry';
const CACHE_VERSION_KEY = 'failed_image_urls_version';
const CACHE_VERSION = '3'; // v3: limpa timeouts falsos em URLs R2 duráveis
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutos

// Cache em memória para performance
let memoryCache: Set<string> | null = null;

/** Foto de perfil do Instagram (Graph devolve cdninstagram.com / fbcdn). Hotlink no browser = 403. */
export function isInstagramCdnAvatarUrl(url?: string | null): boolean {
    if (!url) return false;
    return /cdninstagram\.com|fbcdn\.net/i.test(url);
}

/**
 * Avatar que a UI pode carregar: só URL permanente (R2).
 * `pps.whatsapp.net` / IG CDN / mmg = 403 no browser — nunca hotlink.
 */
export function canRenderRemoteAvatarUrl(url?: string | null): boolean {
    if (!url) return false;
    if (url.includes('NO+URL') || url.includes('ui-avatars.com')) return false;
    return !isTemporaryRemoteMediaUrl(url);
}

/** CDN remota efêmera (WA/IG/FB/Uazapi). R2 permanente fica de fora. */
export function isTemporaryRemoteMediaUrl(url?: string | null): boolean {
    if (!url) return false;
    if (/r2\.dev|cloudflare|adlitics|chat-files/i.test(url)) return false;
    return /(^https?:\/\/)?([^/]+\.)?(whatsapp\.net|fbcdn\.net|cdninstagram\.com|uazapi\.com|fbsbx\.com)\//i.test(url);
}

/** Preview local (optimistic) — navegador consegue renderizar sem hotlink. */
export function isLocalMediaUrl(url?: string | null): boolean {
    return Boolean(url && (url.startsWith('blob:') || url.startsWith('data:')));
}

/** URL que a bolha pode exibir agora (local ou permanente; nunca CDN efêmera). */
export function isDisplayableMediaUrl(url?: string | null): boolean {
    if (!url) return false;
    // Stickers animados Uazapi às vezes viram .zip (Lottie) — <img> quebra com ícone "Sticker".
    if (/\.(zip|json)(\?|#|$)/i.test(url)) return false;
    return isLocalMediaUrl(url) || !isTemporaryRemoteMediaUrl(url);
}

/**
 * Escolhe URL de mídia para a UI: permanente > blob/data local > vazio.
 * Nunca promove CDN efêmera (403 no browser → “indisponível”).
 */
export function preferDisplayMediaUrl(
    current?: string | null,
    incoming?: string | null,
): string | undefined {
    const cur = current || undefined;
    const inc = incoming || undefined;
    if (!inc) return isDisplayableMediaUrl(cur) ? cur : undefined;
    if (!cur) return isDisplayableMediaUrl(inc) ? inc : undefined;
    if (isTemporaryRemoteMediaUrl(inc) && isDisplayableMediaUrl(cur)) return cur;
    if (isLocalMediaUrl(cur) && isDisplayableMediaUrl(inc) && !isLocalMediaUrl(inc)) return inc;
    if (isDisplayableMediaUrl(inc)) return inc;
    return isDisplayableMediaUrl(cur) ? cur : undefined;
}

/**
 * Não trocar URL permanente (R2) por CDN efêmera no reconnect/polling.
 * Incoming vazio → mantém current.
 */
export function preferPermanentAvatarUrl(
    current?: string | null,
    incoming?: string | null,
): string | null {
    if (!incoming) return current || null;
    if (!current) return incoming;
    if (isTemporaryRemoteMediaUrl(incoming) && !isTemporaryRemoteMediaUrl(current)) {
        return current;
    }
    return incoming;
}

/**
 * Carrega o cache do localStorage para memória.
 * Verifica versão e expiração antes de utilizar dados existentes.
 */
const loadCache = (): Set<string> => {
    if (memoryCache) return memoryCache;

    try {
        // Se a versão mudou, descarta tudo (resolve caches antigos de 24h)
        const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
        if (storedVersion !== CACHE_VERSION) {
            localStorage.removeItem(CACHE_KEY);
            localStorage.removeItem(CACHE_EXPIRY_KEY);
            localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
            memoryCache = new Set<string>();
            return memoryCache;
        }

        const expiry = localStorage.getItem(CACHE_EXPIRY_KEY);
        const now = Date.now();

        // Se expirou, limpa o cache
        if (expiry && now > parseInt(expiry, 10)) {
            localStorage.removeItem(CACHE_KEY);
            localStorage.removeItem(CACHE_EXPIRY_KEY);
            memoryCache = new Set<string>();
            return memoryCache;
        }

        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            const urls = JSON.parse(cached) as string[];
            memoryCache = new Set(urls);
        } else {
            memoryCache = new Set<string>();
        }
    } catch {
        memoryCache = new Set<string>();
    }

    return memoryCache;
};

/**
 * Salva o cache em localStorage
 */
const saveCache = () => {
    try {
        const cache = loadCache();
        // Limita a 200 URLs para não estourar localStorage
        const urls = Array.from(cache).slice(-200);
        localStorage.setItem(CACHE_KEY, JSON.stringify(urls));

        // Define expiração se não existir
        if (!localStorage.getItem(CACHE_EXPIRY_KEY)) {
            localStorage.setItem(CACHE_EXPIRY_KEY, String(Date.now() + CACHE_DURATION_MS));
        }
    } catch {
        // Ignora erros de localStorage cheio
    }
};

/**
 * Marca uma URL de imagem como falha (apenas URLs reais, nunca temporárias do WhatsApp)
 */
export const markImageAsFailed = (url: string) => {
    if (!url) return;
    // Blob/data e CDN efêmera: falha esperada no swap — não poluir o cache
    if (isLocalMediaUrl(url)) return;
    if ((isTemporaryRemoteMediaUrl(url) && !isInstagramCdnAvatarUrl(url)) || url.includes('supabase.co/storage')) return;
    const cache = loadCache();
    cache.add(url);
    saveCache();
};

/**
 * Verifica se uma URL de imagem já falhou anteriormente.
 * Retorna false para URLs nulas/undefined — ausência de URL NÃO é uma falha.
 */
export const isImageFailed = (url: string | undefined | null): boolean => {
    if (!url) return false;
    const cache = loadCache();
    return cache.has(url);
};

/**
 * Limpa o cache de imagens falhas (útil para debug)
 */
export const clearFailedImageCache = () => {
    memoryCache = new Set<string>();
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_EXPIRY_KEY);
};
